<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="consultant-pay-bill-details-modal-overlay"
    >
      <div
        class="consultant-pay-bill-details-modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="consultant-pay-bill-details-modal-dialog-title"
        title="Drag the bottom-right corner to resize"
        @click.stop
      >
        <header class="consultant-pay-bill-details-modal-header">
          <div class="consultant-pay-bill-details-modal-heading">
            <h2 id="consultant-pay-bill-details-modal-dialog-title" class="consultant-pay-bill-details-modal-sr-only">
              {{ accessibilityTitle }}
            </h2>
            <div class="consultant-pay-bill-details-modal-row-primary-wrap">
              <div class="consultant-pay-bill-details-modal-row-primary">
                <span v-if="displayVendor" class="consultant-pay-bill-details-modal-customer-name">{{ displayVendor }}</span>
                <template v-if="displayVendor && payTitleTrim">
                  <span class="consultant-pay-bill-details-modal-primary-sep" aria-hidden="true">—</span>
                </template>
                <span v-if="payTitleTrim" class="consultant-pay-bill-details-modal-invoice-no">
                  Pay bill: <strong>{{ payTitleTrim }}</strong>
                </span>
                <span v-else-if="!displayVendor" class="consultant-pay-bill-details-modal-invoice-no">Pay bill details</span>
                <div
                  v-if="headerMetaChips.length"
                  class="consultant-pay-bill-details-modal-primary-meta"
                >
                  <div
                    v-for="(chip, idx) in headerMetaChips"
                    :key="'pm-' + idx"
                    class="consultant-pay-bill-details-modal-detail-chip"
                  >
                    <span class="consultant-pay-bill-details-modal-detail-k">{{ chip.label }}</span>
                    <span class="consultant-pay-bill-details-modal-detail-v">{{ chip.value }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-if="moneySummary"
              class="consultant-pay-bill-details-modal-row-details-wrap"
            >
              <div class="consultant-pay-bill-details-modal-row-details">
                <div
                  v-if="hasMoney(moneySummary.total)"
                  class="consultant-pay-bill-details-modal-money consultant-pay-bill-details-modal-money--inline consultant-pay-bill-details-modal-money--emph"
                >
                  <span>Payment total</span>
                  <span>{{ formatCurrency(moneySummary.total) }}</span>
                </div>
                <div
                  v-if="depositInDisplay"
                  class="consultant-pay-bill-details-modal-money consultant-pay-bill-details-modal-money--inline consultant-pay-bill-details-modal-money--rp-deposit"
                >
                  <span>Pay from</span>
                  <span>{{ depositInDisplay }}</span>
                </div>
                <button
                  v-if="payBillComments"
                  type="button"
                  class="consultant-pay-bill-details-modal-memo-btn"
                  :aria-expanded="memoOpen ? 'true' : 'false'"
                  @click="memoOpen = !memoOpen"
                >
                  Memo
                </button>
              </div>
            </div>
          </div>
          <button
            type="button"
            class="consultant-pay-bill-details-modal-close"
            aria-label="Close"
            title="Close"
            @click="handleClose"
          >
            <i class="bi bi-x-lg" aria-hidden="true"></i>
          </button>
        </header>

        <div class="consultant-pay-bill-details-modal-body">
            <section
              v-show="memoOpen && payBillComments && !payLoading && !payError"
              class="consultant-pay-bill-details-modal-summary consultant-pay-bill-details-modal-summary--notes-only"
            >
              <div class="consultant-pay-bill-details-modal-extra">
                <div class="consultant-pay-bill-details-modal-extra-line consultant-pay-bill-details-modal-extra-line--memo">
                  <span class="consultant-pay-bill-details-modal-extra-k">Memo</span>
                  <span class="consultant-pay-bill-details-modal-extra-v">{{ payBillComments }}</span>
                </div>
              </div>
            </section>

            <nav
              v-if="canViewJournalEntries && !payLoading && !payError"
              class="consultant-pay-bill-details-modal-tabs"
              role="tablist"
              aria-label="Pay bill details"
            >
              <button
                type="button"
                role="tab"
                class="consultant-pay-bill-details-modal-tab"
                :class="{ 'consultant-pay-bill-details-modal-tab--active': bodyView === 'lines' }"
                :aria-selected="bodyView === 'lines' ? 'true' : 'false'"
                @click="switchBodyTab('lines')"
              >
                Pay bill lines
              </button>
              <button
                type="button"
                role="tab"
                class="consultant-pay-bill-details-modal-tab"
                :class="{ 'consultant-pay-bill-details-modal-tab--active': bodyView === 'journal' }"
                :aria-selected="bodyView === 'journal' ? 'true' : 'false'"
                @click="switchBodyTab('journal')"
              >
                Journal entries
              </button>
            </nav>

            <div class="consultant-pay-bill-details-modal-tab-panel" role="tabpanel">
            <div v-if="payLoading" class="consultant-pay-bill-details-modal-empty consultant-pay-bill-details-modal-empty--compact">
              Loading payment…
            </div>
            <div v-else-if="payError" class="consultant-pay-bill-details-modal-empty consultant-pay-bill-details-modal-empty--compact">
              <p class="consultant-pay-bill-details-modal-je-error">{{ payError }}</p>
              <button type="button" class="consultant-pay-bill-details-modal-je-retry" @click="loadPayBillDetail">
                Retry
              </button>
            </div>
            <template v-else-if="bodyView === 'lines'">
              <div v-if="!hasAnyPayBillLines" class="consultant-pay-bill-details-modal-empty">
                No payment allocation lines for this pay bill.
              </div>
              <div v-else class="consultant-pay-bill-details-modal-table-scroll">
                <table
                  v-if="manualAccountAllocationRows.length"
                  class="consultant-pay-bill-details-modal-table consultant-pay-bill-details-modal-table--receipt"
                >
                  <thead>
                    <tr>
                      <th scope="col" class="consultant-pay-bill-details-modal-th">Account</th>
                      <th scope="col" class="consultant-pay-bill-details-modal-th">Bill #</th>
                      <th scope="col" class="consultant-pay-bill-details-modal-th">Type</th>
                      <th scope="col" class="consultant-pay-bill-details-modal-th">Memo</th>
                      <th scope="col" class="consultant-pay-bill-details-modal-th consultant-pay-bill-details-modal-th--num">Applied</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in manualAccountAllocationRows"
                      :key="'pa-' + row.id"
                      class="consultant-pay-bill-details-modal-tr"
                      :class="{ 'consultant-pay-bill-details-modal-tr--highlight': isHighlightedAllocationRow(row) }"
                    >
                      <td class="consultant-pay-bill-details-modal-td">{{ row.accountName || '—' }}</td>
                      <td class="consultant-pay-bill-details-modal-td">
                        <button
                          v-if="canOpenBill(row)"
                          type="button"
                          class="consultant-pay-bill-details-modal-bill-link"
                          :title="`View bill ${row.billNo}`"
                          @click="openBillForRow(row)"
                        >
                          {{ row.billNo || '—' }}
                        </button>
                        <span v-else>{{ row.billNo || '—' }}</span>
                      </td>
                      <td class="consultant-pay-bill-details-modal-td">{{ formatRecordType(row.recordType) }}</td>
                      <td class="consultant-pay-bill-details-modal-td consultant-pay-bill-details-modal-td--desc">{{ row.memo || '—' }}</td>
                      <td
                        class="consultant-pay-bill-details-modal-td consultant-pay-bill-details-modal-td--num consultant-pay-bill-details-modal-td--rp-applied"
                      >
                        {{ formatCurrency(row.amountPaid) }}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <table
                  v-if="allocationRows.length"
                  class="consultant-pay-bill-details-modal-table consultant-pay-bill-details-modal-table--receipt consultant-pay-bill-details-modal-table--spaced"
                >
                  <thead>
                    <tr>
                      <th scope="col" class="consultant-pay-bill-details-modal-th">Consultant</th>
                      <th scope="col" class="consultant-pay-bill-details-modal-th">Bill #</th>
                      <th scope="col" class="consultant-pay-bill-details-modal-th">Type</th>
                      <th scope="col" class="consultant-pay-bill-details-modal-th">Memo</th>
                      <th scope="col" class="consultant-pay-bill-details-modal-th consultant-pay-bill-details-modal-th--num">Applied</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in allocationRows"
                      :key="'pr-' + row.id"
                      class="consultant-pay-bill-details-modal-tr"
                      :class="{ 'consultant-pay-bill-details-modal-tr--highlight': isHighlightedAllocationRow(row) }"
                    >
                      <td class="consultant-pay-bill-details-modal-td">{{ row.consultantName || '—' }}</td>
                      <td class="consultant-pay-bill-details-modal-td">
                        <button
                          v-if="canOpenBill(row)"
                          type="button"
                          class="consultant-pay-bill-details-modal-bill-link"
                          :title="`View bill ${row.billNo}`"
                          @click="openBillForRow(row)"
                        >
                          {{ row.billNo || '—' }}
                        </button>
                        <span v-else>{{ row.billNo || '—' }}</span>
                      </td>
                      <td class="consultant-pay-bill-details-modal-td">{{ formatRecordType(row.recordType) }}</td>
                      <td class="consultant-pay-bill-details-modal-td consultant-pay-bill-details-modal-td--desc">{{ row.memo || '—' }}</td>
                      <td
                        class="consultant-pay-bill-details-modal-td consultant-pay-bill-details-modal-td--num consultant-pay-bill-details-modal-td--rp-applied"
                      >
                        {{ formatCurrency(row.amountPaid) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <table
                  v-if="itemAllocationRows.length"
                  class="consultant-pay-bill-details-modal-table consultant-pay-bill-details-modal-table--receipt consultant-pay-bill-details-modal-table--spaced"
                >
                  <thead>
                    <tr>
                      <th scope="col" class="consultant-pay-bill-details-modal-th">Item / service</th>
                      <th scope="col" class="consultant-pay-bill-details-modal-th">Bill #</th>
                      <th scope="col" class="consultant-pay-bill-details-modal-th">Type</th>
                      <th scope="col" class="consultant-pay-bill-details-modal-th">Memo</th>
                      <th scope="col" class="consultant-pay-bill-details-modal-th consultant-pay-bill-details-modal-th--num">Applied</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in itemAllocationRows"
                      :key="'pi-' + row.id"
                      class="consultant-pay-bill-details-modal-tr"
                      :class="{ 'consultant-pay-bill-details-modal-tr--highlight': isHighlightedAllocationRow(row) }"
                    >
                      <td class="consultant-pay-bill-details-modal-td">{{ row.itemName || '—' }}</td>
                      <td class="consultant-pay-bill-details-modal-td">
                        <button
                          v-if="canOpenBill(row)"
                          type="button"
                          class="consultant-pay-bill-details-modal-bill-link"
                          :title="`View bill ${row.billNo}`"
                          @click="openBillForRow(row)"
                        >
                          {{ row.billNo || '—' }}
                        </button>
                        <span v-else>{{ row.billNo || '—' }}</span>
                      </td>
                      <td class="consultant-pay-bill-details-modal-td">{{ formatRecordType(row.recordType) }}</td>
                      <td class="consultant-pay-bill-details-modal-td consultant-pay-bill-details-modal-td--desc">{{ row.memo || '—' }}</td>
                      <td
                        class="consultant-pay-bill-details-modal-td consultant-pay-bill-details-modal-td--num consultant-pay-bill-details-modal-td--rp-applied"
                      >
                        {{ formatCurrency(row.amountPaid) }}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <table
                  v-if="billLevelAllocationRows.length"
                  class="consultant-pay-bill-details-modal-table consultant-pay-bill-details-modal-table--receipt consultant-pay-bill-details-modal-table--spaced"
                >
                  <thead>
                    <tr>
                      <th scope="col" class="consultant-pay-bill-details-modal-th">Bill #</th>
                      <th scope="col" class="consultant-pay-bill-details-modal-th">Type</th>
                      <th scope="col" class="consultant-pay-bill-details-modal-th">Memo</th>
                      <th scope="col" class="consultant-pay-bill-details-modal-th consultant-pay-bill-details-modal-th--num">Applied</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in billLevelAllocationRows"
                      :key="'pb-' + row.id"
                      class="consultant-pay-bill-details-modal-tr"
                      :class="{ 'consultant-pay-bill-details-modal-tr--highlight': isHighlightedAllocationRow(row) }"
                    >
                      <td class="consultant-pay-bill-details-modal-td">
                        <button
                          v-if="canOpenBill(row)"
                          type="button"
                          class="consultant-pay-bill-details-modal-bill-link"
                          :title="`View bill ${row.billNo}`"
                          @click="openBillForRow(row)"
                        >
                          {{ row.billNo || '—' }}
                        </button>
                        <span v-else>{{ row.billNo || '—' }}</span>
                      </td>
                      <td class="consultant-pay-bill-details-modal-td">Bill</td>
                      <td class="consultant-pay-bill-details-modal-td consultant-pay-bill-details-modal-td--desc">{{ row.memo || '—' }}</td>
                      <td
                        class="consultant-pay-bill-details-modal-td consultant-pay-bill-details-modal-td--num consultant-pay-bill-details-modal-td--rp-applied"
                      >
                        {{ formatCurrency(row.amountPaid) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>

            <div v-else class="consultant-pay-bill-details-modal-table-scroll">
              <div v-if="journalLoading" class="consultant-pay-bill-details-modal-empty consultant-pay-bill-details-modal-empty--compact">
                Loading journal entries…
              </div>
              <div v-else-if="journalError" class="consultant-pay-bill-details-modal-empty consultant-pay-bill-details-modal-empty--compact">
                <p class="consultant-pay-bill-details-modal-je-error">{{ journalError }}</p>
                <button type="button" class="consultant-pay-bill-details-modal-je-retry" @click="loadJournalEntriesForPayBill">
                  Retry
                </button>
              </div>
              <div v-else-if="!flattenedJournalRows.length" class="consultant-pay-bill-details-modal-empty consultant-pay-bill-details-modal-empty--compact">
                No journal entries linked to this pay bill.
              </div>
              <table v-else class="consultant-pay-bill-details-modal-table consultant-pay-bill-details-modal-table--journal">
                <thead>
                  <tr>
                    <th scope="col" class="consultant-pay-bill-details-modal-th">Entry #</th>
                    <th scope="col" class="consultant-pay-bill-details-modal-th">Date</th>
                    <th scope="col" class="consultant-pay-bill-details-modal-th">Status</th>
                    <th scope="col" class="consultant-pay-bill-details-modal-th">Account</th>
                    <th scope="col" class="consultant-pay-bill-details-modal-th">Memo</th>
                    <th scope="col" class="consultant-pay-bill-details-modal-th consultant-pay-bill-details-modal-th--num">Debit</th>
                    <th scope="col" class="consultant-pay-bill-details-modal-th consultant-pay-bill-details-modal-th--num">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in flattenedJournalRows" :key="row.rowKey" class="consultant-pay-bill-details-modal-tr">
                    <td class="consultant-pay-bill-details-modal-td">{{ row.entryNo || '—' }}</td>
                    <td class="consultant-pay-bill-details-modal-td">{{ formatDate(row.entryDate) }}</td>
                    <td class="consultant-pay-bill-details-modal-td">{{ formatStatus(row.status) }}</td>
                    <td class="consultant-pay-bill-details-modal-td">{{ row.accountName }}</td>
                    <td class="consultant-pay-bill-details-modal-td consultant-pay-bill-details-modal-td--desc">{{ row.memo }}</td>
                    <td class="consultant-pay-bill-details-modal-td consultant-pay-bill-details-modal-td--num consultant-pay-bill-details-modal-td--je-debit">
                      {{ formatCurrency(row.debit) }}
                    </td>
                    <td class="consultant-pay-bill-details-modal-td consultant-pay-bill-details-modal-td--num consultant-pay-bill-details-modal-td--je-credit">
                      {{ formatCurrency(row.credit) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            </div>
        </div>
      </div>
    </div>

    <BillDetailsModal
      :show="showBillModal"
      stacked
      show-back
      back-label="Back to pay bill"
      :company-id="companyId"
      :enter-bill-id="billModalEnterBillId"
      :bill-title="billModalTitle"
      :vendor-name="billModalVendor"
      :highlight-consultant-bill-record-id="billModalHighlightConsultantBillRecordId"
      @back="closeBillModal"
      @close="closeBillModal"
    />
  </Teleport>
</template>

<script>
import './PayBillDetailsModal.css';
import BillDetailsModal from '@/components/BillDetailsModal.vue';
import ApiService from '@/services/api.service.js';
import JournalEntryService from '@/services/journalEntry.service.js';
import logger from '@/utils/logger';

export default {
  name: 'PayBillDetailsModal',
  components: { BillDetailsModal },
  props: {
    show: { type: Boolean, default: false },
    companyId: { type: [String, Number], default: null },
    vendorPayBillId: { type: [String, Number], default: null },
    payBillNo: { type: String, default: '' },
    vendorName: { type: String, default: '' },
    /** VendorPayBillRecord ids to highlight (e.g. this consultant’s allocations) */
    highlightPayBillRecordIds: {
      type: Array,
      default: () => []
    }
  },
  emits: ['close'],
  data() {
    return {
      payRows: [],
      payLoading: false,
      payError: '',
      bodyView: 'lines',
      memoOpen: false,
      journalEntries: [],
      journalLoading: false,
      journalError: '',
      journalLoadedKey: null,
      showBillModal: false,
      billModalEnterBillId: null,
      billModalTitle: '',
      billModalVendor: '',
      billModalHighlightConsultantBillRecordId: null
    };
  },
  computed: {
    payTitleTrim() {
      return (this.payBillNo || '').trim();
    },
    firstRow() {
      return Array.isArray(this.payRows) && this.payRows.length ? this.payRows[0] : null;
    },
    displayVendor() {
      const fromRow = this.firstRow?.vendor?.vendorName;
      const fromProp = (this.vendorName || '').trim();
      return (fromRow || fromProp || '').trim();
    },
    payBillComments() {
      const c = this.firstRow?.payBillComments;
      return c ? String(c).trim() : '';
    },
    depositInDisplay() {
      const acc = this.firstRow?.payFromAccount;
      if (!acc) return '';
      const name = acc.accountName || '';
      const num = acc.accountNumber ? ` (${acc.accountNumber})` : '';
      const t = `${name}${num}`.trim();
      return t;
    },
    accessibilityTitle() {
      const v = this.displayVendor;
      const p = this.payTitleTrim;
      if (v && p) return `${v}, Pay bill ${p}`;
      if (p) return `Pay bill ${p}`;
      if (v) return v;
      return 'Pay bill details';
    },
    canViewJournalEntries() {
      const pid = this.vendorPayBillId;
      const hasPay =
        pid !== null && pid !== undefined && pid !== '';
      const hasCompany =
        this.companyId !== null && this.companyId !== undefined && this.companyId !== '';
      return hasPay && hasCompany;
    },
    journalCacheKey() {
      return `paybill:${this.companyId}:${this.vendorPayBillId}`;
    },
    headerMetaChips() {
      const r = this.firstRow;
      if (!r) return [];
      const chips = [];
      if (r.payBillDate) chips.push({ label: 'Payment date', value: this.formatDate(r.payBillDate) });
      if (r.paymentMethod) chips.push({ label: 'Method', value: String(r.paymentMethod) });
      return chips;
    },
    moneySummary() {
      const r = this.firstRow;
      if (!r) return null;
      const total = r.payBillTotal != null ? r.payBillTotal : null;
      return { total, paid: null, balance: null };
    },
    hasAnyPayBillLines() {
      return (
        this.manualAccountAllocationRows.length > 0 ||
        this.allocationRows.length > 0 ||
        this.itemAllocationRows.length > 0 ||
        this.billLevelAllocationRows.length > 0
      );
    },
    manualAccountAllocationRows() {
      if (!Array.isArray(this.payRows)) return [];
      const rows = [];
      for (const raw of this.payRows) {
        const recordType = String(raw.recordType || '').toLowerCase();
        const bill = raw.bill || {};
        const enterBillId = bill.id ?? raw.billId ?? null;
        const billNo = bill.billNo || '—';

        if (recordType === 'account') {
          const accountMatch = this.findManualAccountRecord(bill, raw.recordId);
          if (!accountMatch) continue;
          rows.push(this.buildManualAccountAllocationRow(raw, accountMatch, bill, enterBillId, billNo));
          continue;
        }

        if (!recordType) {
          const manualAccounts = this.getManualAccountRecordsForBill(bill);
          if (!manualAccounts.length) continue;
          const amountPaid = Number(raw.amountPaid || 0);
          if (manualAccounts.length === 1) {
            rows.push(
              this.buildManualAccountAllocationRow(raw, manualAccounts[0], bill, enterBillId, billNo, amountPaid)
            );
          } else {
            manualAccounts.forEach((account) => {
              const linePaid =
                Number(account.paidAmount || 0) > 0 ? Number(account.paidAmount) : amountPaid / manualAccounts.length;
              rows.push(
                this.buildManualAccountAllocationRow(raw, account, bill, enterBillId, billNo, linePaid)
              );
            });
          }
        }
      }
      return rows;
    },
    itemAllocationRows() {
      if (!Array.isArray(this.payRows)) return [];
      return this.payRows
        .filter((raw) => String(raw.recordType || '').toLowerCase() === 'item')
        .map((raw) => {
          const bill = raw.bill || {};
          const enterBillId = bill.id ?? raw.billId ?? null;
          const billNo = bill.billNo || '—';
          const recordId = raw.recordId != null ? raw.recordId : '';
          const items = bill.itemRecords || bill.items || [];
          const itemMatch = items.find(
            (it) => it && it.id != null && String(it.id) === String(recordId)
          );
          const itemName =
            itemMatch?.itemName ||
            itemMatch?.productName ||
            itemMatch?.serviceName ||
            itemMatch?.description ||
            '—';
          const memo =
            (itemMatch?.description && String(itemMatch.description).trim()) ||
            (raw.description && String(raw.description).trim()) ||
            '';
          return {
            id: raw.id,
            enterBillId,
            billNo,
            itemName,
            recordType: 'item',
            memo,
            amountPaid: raw.amountPaid,
            recordId: raw.recordId,
            rawRecordType: 'item'
          };
        });
    },
    billLevelAllocationRows() {
      if (!Array.isArray(this.payRows)) return [];
      return this.payRows
        .filter((raw) => {
          const recordType = String(raw.recordType || '').toLowerCase();
          if (recordType) return false;
          const bill = raw.bill || {};
          // Already represented via manual account split
          if (this.getManualAccountRecordsForBill(bill).length > 0) return false;
          return Number(raw.amountPaid || 0) !== 0 || raw.billId != null;
        })
        .map((raw) => {
          const bill = raw.bill || {};
          const enterBillId = bill.id ?? raw.billId ?? null;
          const billNo = bill.billNo || '—';
          const memo =
            (raw.description && String(raw.description).trim()) ||
            (bill.comments && String(bill.comments).trim()) ||
            '';
          return {
            id: raw.id,
            enterBillId,
            billNo,
            recordType: 'bill',
            memo,
            amountPaid: raw.amountPaid,
            recordId: raw.recordId,
            rawRecordType: null
          };
        });
    },
    allocationRows() {
      if (!Array.isArray(this.payRows)) return [];
      return this.payRows
        .filter((raw) => String(raw.recordType || '').toLowerCase() === 'consultant')
        .map((raw) => {
        const bill = raw.bill || {};
        const enterBillId = bill.id ?? raw.billId ?? null;
        const billNo = bill.billNo || '—';
        const billDate = bill.date || null;
        const recordType = raw.recordType || '—';
        const recordId = raw.recordId != null ? raw.recordId : '';
        const consultants = bill.consultantRecords || [];
        const consultantMatch = consultants.find(
          (c) => c && c.id != null && String(c.id) === String(recordId)
        );
        const consultantName =
          consultantMatch?.consultantName ||
          consultantMatch?.consultant?.consultantName ||
          '—';
        const memo =
          (consultantMatch?.description && String(consultantMatch.description).trim()) ||
          (raw.description && String(raw.description).trim()) ||
          '';
        return {
          id: raw.id,
          enterBillId,
          billNo,
          billDate,
          consultantName,
          recordType,
          memo,
          amountPaid: raw.amountPaid,
          recordId: raw.recordId,
          rawRecordType: raw.recordType
        };
      });
    },
    flattenedJournalRows() {
      const entries = this.journalEntries;
      if (!Array.isArray(entries) || entries.length === 0) return [];
      const rows = [];
      for (const entry of entries) {
        const base = {
          entryNo: entry.entryNo,
          entryDate: entry.entryDate,
          status: entry.status
        };
        const entryLines = Array.isArray(entry.entryLines) ? entry.entryLines : [];
        if (entryLines.length === 0) {
          rows.push({
            ...base,
            accountName: '—',
            memo: entry.comments ? String(entry.comments) : '—',
            debit: null,
            credit: null,
            rowKey: `je-${entry.id}-empty`
          });
        } else {
          for (const line of entryLines) {
            const lid = line.id != null ? line.id : `${line.accountId}-${rows.length}`;
            rows.push({
              ...base,
              accountName: line.account?.accountName || '—',
              memo: (line.memo && String(line.memo).trim()) ? line.memo : '—',
              debit: line.debit,
              credit: line.credit,
              rowKey: `je-${entry.id}-ln-${lid}`
            });
          }
        }
      }
      return rows;
    }
  },
  watch: {
    show(val) {
      if (!val) {
        this.resetAll();
      } else {
        this.bodyView = 'lines';
        this.memoOpen = false;
        this.journalEntries = [];
        this.journalError = '';
        this.journalLoading = false;
        this.journalLoadedKey = null;
        this.loadPayBillDetail();
      }
    },
    vendorPayBillId() {
      if (this.show) {
        this.resetAll();
        this.bodyView = 'lines';
        this.loadPayBillDetail();
      }
    }
  },
  methods: {
    getManualAccountRecordsForBill(bill) {
      const list = bill?.accountRecords || [];
      if (!Array.isArray(list)) return [];
      return list.filter((row) => row.isManualRecord === true);
    },
    findManualAccountRecord(bill, recordId) {
      if (recordId === null || recordId === undefined || recordId === '') return null;
      const list = this.getManualAccountRecordsForBill(bill);
      return list.find((row) => row && row.id != null && String(row.id) === String(recordId)) || null;
    },
    formatAccountDisplayName(account) {
      if (!account) return '—';
      if (account.account) {
        const accountNumber = account.account.accountNumber || '';
        const accountName = account.account.accountName || '';
        return accountNumber ? `${accountNumber} - ${accountName}` : accountName || '—';
      }
      return account.accountName || account.description || '—';
    },
    buildManualAccountAllocationRow(raw, accountMatch, bill, enterBillId, billNo, amountPaidOverride) {
      const memo =
        (accountMatch?.description && String(accountMatch.description).trim()) ||
        (raw.description && String(raw.description).trim()) ||
        '';
      return {
        id: raw.id,
        enterBillId,
        billNo,
        accountName: this.formatAccountDisplayName(accountMatch),
        recordType: 'account',
        memo,
        amountPaid: amountPaidOverride != null ? amountPaidOverride : raw.amountPaid,
        recordId: raw.recordId,
        rawRecordType: 'account'
      };
    },
    isHighlightedAllocationRow(row) {
      const ids = this.highlightPayBillRecordIds;
      if (!Array.isArray(ids) || ids.length === 0 || !row || row.id == null) return false;
      return ids.some((id) => String(id) === String(row.id));
    },
    resetAll() {
      this.closeBillModal();
      this.payRows = [];
      this.payLoading = false;
      this.payError = '';
      this.bodyView = 'lines';
      this.memoOpen = false;
      this.journalEntries = [];
      this.journalError = '';
      this.journalLoading = false;
      this.journalLoadedKey = null;
    },
    async loadPayBillDetail() {
      const id = this.vendorPayBillId;
      if (id === null || id === undefined || id === '') {
        this.payError = 'Missing pay bill id.';
        return;
      }
      this.payLoading = true;
      this.payError = '';
      try {
        const data = await ApiService.getPayBillRecordsByPayBillId(id);
        this.payRows = Array.isArray(data) ? data : [];
        if (this.payRows.length === 0) {
          this.payError = 'No payment records found.';
        }
      } catch (e) {
        logger.error('PayBillDetailsModal: load failed', e);
        this.payError =
          (e && e.response && e.response.data && e.response.data.error) ||
          e.message ||
          'Could not load pay bill.';
        this.payRows = [];
      } finally {
        this.payLoading = false;
      }
    },
    async switchBodyTab(view) {
      if (view !== 'lines' && view !== 'journal') return;
      if (this.bodyView === view) return;
      this.bodyView = view;
      if (view === 'journal') {
        await this.loadJournalEntriesForPayBill();
      }
    },
    async loadJournalEntriesForPayBill() {
      if (!this.canViewJournalEntries) return;
      const key = this.journalCacheKey;
      if (this.journalLoadedKey === key && !this.journalError) return;
      this.journalLoading = true;
      this.journalError = '';
      try {
        const res = await JournalEntryService.getJournalEntriesByVendorPayBillId(
          this.vendorPayBillId,
          this.companyId
        );
        const list = res && Array.isArray(res.data) ? res.data : [];
        this.journalEntries = list;
        this.journalLoadedKey = key;
      } catch (e) {
        logger.error('PayBillDetailsModal: journal fetch failed', e);
        this.journalError =
          (e && e.response && e.response.data && e.response.data.message) ||
          e.message ||
          'Could not load journal entries.';
        this.journalEntries = [];
      } finally {
        this.journalLoading = false;
      }
    },
    handleClose() {
      this.closeBillModal();
      this.$emit('close');
    },
    canOpenBill(row) {
      const id = row?.enterBillId;
      return id !== null && id !== undefined && id !== '';
    },
    openBillForRow(row) {
      if (!this.canOpenBill(row)) return;
      const enterBillId = row.enterBillId;
      this.billModalEnterBillId = enterBillId;
      this.billModalTitle = row.billNo && row.billNo !== '—' ? row.billNo : `#${enterBillId}`;
      this.billModalVendor = this.displayVendor;
      const isConsultant =
        String(row.rawRecordType || row.recordType || '').toLowerCase() === 'consultant';
      this.billModalHighlightConsultantBillRecordId =
        isConsultant && row.recordId != null && row.recordId !== '' ? row.recordId : null;
      this.showBillModal = true;
    },
    closeBillModal() {
      this.showBillModal = false;
      this.billModalEnterBillId = null;
      this.billModalTitle = '';
      this.billModalVendor = '';
      this.billModalHighlightConsultantBillRecordId = null;
    },
    hasMoney(v) {
      if (v === undefined || v === null || v === '') return false;
      const n = Number(v);
      return !Number.isNaN(n);
    },
    formatDate(dateString) {
      if (!dateString) return '—';
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return '—';
      return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    },
    formatStatus(status) {
      if (!status) return '—';
      return String(status)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    formatRecordType(rt) {
      if (!rt || rt === '—') return '—';
      const t = String(rt).toLowerCase();
      if (t === 'consultant') return 'Consultant';
      if (t === 'item') return 'Item';
      if (t === 'account') return 'Account';
      return String(rt);
    },
    formatCurrency(amount) {
      if (amount === undefined || amount === null || amount === '') return '—';
      const n = Number(amount);
      if (Number.isNaN(n)) return '—';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(n);
    }
  }
};
</script>
